import React, { useEffect, useState, useCallback } from "react";
import { useContext } from "react";
import { AuthContext } from "../AuthContext";
import axiosInstance from "../axiosConfig";
import "../assets/CSS/Header.css"; 
import logo from "../assets/Img/Logo-1.png";
import User from "../assets/Img/User.gif";
import Bell from "../assets/Img/BellIcon.png";
import moment from "moment-timezone";
import Dispatch from "../assets/Img/Dispatch.png";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2"; // ✅ Imported SweetAlert2

const Header = ({ toggleSidebar, isSidebarVisible }) => {
  const { logout } = useContext(AuthContext);

  const user = JSON.parse(localStorage.getItem("user"));
  const [processData, setProcessData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [delayedProcesses, setDelayedProcesses] = useState([]); 
  const [showDelayedModal, setShowDelayedModal] = useState(false); 
  const [delayed24hrProcesses, setDelayed24hrProcesses] = useState([]); 
  const [showDelayed24hrModal, setShowDelayed24hrModal] = useState(false); 
  
  const [dispatchOrders, setDispatchOrders] = useState([]);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchCount, setDispatchCount] = useState(0);
  const [remarkInput, setRemarkInput] = useState({});
  const [dispatchQtyInput, setDispatchQtyInput] = useState({});

  const [liveRemainingTimes, setLiveRemainingTimes] = useState({});

  const [workerAllocations, setWorkerAllocations] = useState([]);
  const [showAllocationModal, setShowAllocationModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [lotIdFilter, setLotIdFilter] = useState("");
  const filteredDispatchOrders = dispatchOrders.filter(order => lotIdFilter === "" || (order["LOT ID"] && order["LOT ID"].toString().includes(lotIdFilter)));

  const [processItemFilter, setProcessItemFilter] = useState("");
  const filteredProcessData = processData.filter(process => {
    // Hide processes that have already been marked as Completed
    if (process.CompletedTime != null) return false;

    return processItemFilter === "" || 
      (process["ITEM NAME"] && process["ITEM NAME"].toString().toLowerCase().includes(processItemFilter.toLowerCase())) ||
      (process["PROCESS NAME"] && process["PROCESS NAME"].toString().toLowerCase().includes(processItemFilter.toLowerCase()));
  });

  const [delayedFilter, setDelayedFilter] = useState("");
  const filteredDelayedProcesses = delayedProcesses.filter(process => {
    return delayedFilter === "" || 
      (process["SupervisorName"] && process["SupervisorName"].toString().toLowerCase().includes(delayedFilter.toLowerCase())) ||
      (process["PROCESS NAME"] && process["PROCESS NAME"].toString().toLowerCase().includes(delayedFilter.toLowerCase())) ||
      (process["ITEM NAME"] && process["ITEM NAME"].toString().toLowerCase().includes(delayedFilter.toLowerCase()));
  });

  const [delayed24hrFilter, setDelayed24hrFilter] = useState("");
  const filteredDelayed24hrProcesses = delayed24hrProcesses.filter(process => {
    return delayed24hrFilter === "" || 
      (process["SupervisorName"] && process["SupervisorName"].toString().toLowerCase().includes(delayed24hrFilter.toLowerCase())) ||
      (process["PROCESS NAME"] && process["PROCESS NAME"].toString().toLowerCase().includes(delayed24hrFilter.toLowerCase())) ||
      (process["ITEM NAME"] && process["ITEM NAME"].toString().toLowerCase().includes(delayed24hrFilter.toLowerCase()));
  });

  const [srpInputs, setSrpInputs] = useState({});
  const [newNotifications, setNewNotifications] = useState([]);
  const [dismissedNotifications, setDismissedNotifications] = useState(() => {
    const saved = localStorage.getItem("dismissedNotifications");
    return saved ? JSON.parse(saved) : [];
  });

  const [processingId, setProcessingId] = useState(null);

  const handleDispatchQtyChange = (id, value) => {
    setDispatchQtyInput((prev) => ({ ...prev, [id]: value }));
  };
  
  const fetchProcessData = useCallback(async () => {
    try {
      const supervisorName = user?.Name; 
      console.log("Axios Base URL in Header:", axiosInstance.defaults.baseURL);
      const response = await axiosInstance.get("/api/first-process-count", {
        params: { supervisorName },
      });
      setProcessData(response.data); 
    } catch (error) {
      console.error("Error fetching process data:", error);
    }
  }, [user?.Name]);

  const fetchWorkerAllocations = useCallback(async () => {
    if (user?.Auth === "Supervisor" && user?.Department) {
      try {
        const response = await axiosInstance.get("/api/worker-allocation-notification");
        const myDept = user.Department.trim();
        
        const pendingForMe = response.data.filter((alloc) => {
          const targetDept = alloc.To_Department ? alloc.To_Department.trim() : "";
          const currentStatus = alloc.Status ? alloc.Status : "Pending";
          
          return targetDept === myDept && (
            currentStatus === "Pending" || 
            currentStatus === "Occupied" || 
            (currentStatus === "Completed" && !alloc.satisfaction_status)
          );
        });
        
        setWorkerAllocations(pendingForMe);
      } catch (error) {
        console.error("Error fetching worker allocations:", error);
      }
    }
  }, [user?.Auth, user?.Department]);

  useEffect(() => {
    fetchProcessData();
    fetchWorkerAllocations(); 

    const fetchDelayedProcesses = async () => {
      try {
        const response = await axiosInstance.get("/api/delayed-processes");
        setDelayedProcesses(response.data); 
      } catch (error) {
        console.error("Error fetching delayed processes:", error);
      }
    };

    if (user?.Auth === "Admin") {
      fetchDelayedProcesses();
    }

    let allocInterval;
    if (user?.Auth === "Supervisor") {
      allocInterval = setInterval(() => {
        fetchWorkerAllocations();
      }, 10000);
    }

    return () => {
      if (allocInterval) clearInterval(allocInterval);
    };
  }, [user?.Name, user?.Auth, fetchProcessData, fetchWorkerAllocations]);


  // ✅ Disable body scroll when any modal is open
  useEffect(() => {
    const isAnyModalOpen = showModal || showDelayedModal || showDelayed24hrModal || showDispatchModal || showAllocationModal;
    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal, showDelayedModal, showDelayed24hrModal, showDispatchModal, showAllocationModal]);


  // ✅ ================= ACTION: CONFIRM ARRIVAL (SWEET ALERT) ================= ✅
  const handleAcceptWorker = async (id) => {
    const result = await Swal.fire({
      title: 'Confirm Arrival?',
      text: "Confirm that this worker has arrived in your department?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, confirm!'
    });

    if (!result.isConfirmed) return;

    setProcessingId(`accept_${id}`); 
    try {
      await axiosInstance.patch(`/api/worker-allocation/confirm/${id}`);
      Swal.fire('Confirmed!', 'Worker arrival confirmed. Status changed to Occupied!', 'success');
      fetchWorkerAllocations(); 
    } catch (error) {
      console.error("Error accepting worker:", error);
      Swal.fire('Error!', 'Failed to confirm arrival.', 'error');
    } finally {
      setProcessingId(null); 
    }
  };

  // ================= ACTION: WORK COMPLETED & SATISFACTION (SWEET ALERT WITH INPUT) ================= 

  // ACTION: ONLY MARK WORK AS COMPLETED
  const handleMarkCompleted = async (id) => {
    const result = await Swal.fire({
      title: 'Mark as Completed?',
      text: "The worker will become 'Available' again.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Complete it!'
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.patch(`/api/worker-allocation/complete/${id}`);
      Swal.fire('Completed!', 'Work completed. Worker is free now. Please give your feedback.', 'success');
      fetchWorkerAllocations(); 
    } catch (error) {
      console.error("Error completing work:", error);
      Swal.fire('Error!', 'Failed to complete work.', 'error');
    }
  };

  // ACTION: GIVE SATISFACTION FEEDBACK
  const handleSatisfaction = async (id, status) => {
    try {
      const { value: comment } = await Swal.fire({
        title: `You selected '${status}'`,
        input: 'text',
        inputLabel: 'Any remarks? (Optional)',
        inputPlaceholder: 'Type your remarks here...',
        showCancelButton: true,
        confirmButtonColor: '#198754',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Submit Feedback'
      });

      if (comment === undefined) return; // User clicked Cancel

      await axiosInstance.patch(`/api/worker-allocation/satisfaction/${id}`, {
        satisfaction_status: status,
        comment: comment
      });
      
      Swal.fire('Success!', 'Feedback submitted successfully!', 'success');
      fetchWorkerAllocations(); 
    } catch (error) {
      console.error("Error submitting feedback:", error);
      Swal.fire('Error!', 'Failed to submit feedback.', 'error');
    }
  };

  const handleConfirm = async (processName, id) => {
    // Step 1: Show Before Photo upload modal
    const { value: formValues, isConfirmed } = await Swal.fire({
      title: '📷 Upload Before Photo',
      html: `
        <div style="font-family: 'Inter', sans-serif; text-align: center;">
          <p style="margin-bottom:16px;color:#64748b;font-size:14px;line-height:1.5;">
            Please capture or upload a <strong>Before Photo</strong> to confirm receipt of this lot.
          </p>
          
          <label for="swal-before-photo" id="dropzone-before-label" style="
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            padding: 60px 24px; border: 2px dashed #cbd5e1; border-radius: 16px;
            background: #f8fafc; cursor: pointer; transition: all 0.2s ease; min-height: 250px;
          ">
            <div id="dropzone-before-content" style="display: flex; flex-direction: column; align-items: center;">
              <div style="background: #d1fae5; width: 64px; height: 64px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 16px;">
                <svg style="color: #10b981; width: 32px; height: 32px;" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </div>
              <span style="font-size: 16px; font-weight: 700; color: #1e293b;">Tap to Capture Photo</span>
              <span style="font-size: 13px; color: #94a3b8; margin-top: 6px; font-weight: 500;">Supports JPG, PNG, HEIC</span>
            </div>

            <div id="swal-before-preview" style="display: none; width: 100%; text-align: center;">
              <img id="swal-before-img" src="" style="max-height: 220px; max-width: 100%; border-radius: 12px; object-fit: cover; box-shadow: 0 8px 24px rgba(16, 185, 129, 0.15);" />
              <div id="swal-before-name" style="margin-top: 16px; font-size: 13px; font-weight: 600; color: #10b981; background: #ecfdf5; padding: 6px 16px; border-radius: 20px; display: inline-block; border: 1px solid #d1fae5;"></div>
            </div>
          </label>
          
          <input type="file" id="swal-before-photo" accept="image/jpeg,image/jpg,image/png,image/heic" capture="environment" style="display: none;" />
          <div id="swal-before-error" style="color: #ef4444; font-size: 13px; margin-top: 16px; display: none; font-weight: 600; background: #fef2f2; padding: 10px 14px; border-radius: 8px; border: 1px solid #fee2e2;"></div>
        </div>
      `,
      confirmButtonText: '✅ Confirm Lot',
      confirmButtonColor: '#198754',
      cancelButtonText: 'Cancel',
      showCancelButton: true,
      focusConfirm: false,
      width: '600px',
      customClass: { popup: 'swal-above-modal' },
      didOpen: () => {
        // Fix z-index so Swal appears above Bootstrap modal, and add backdrop blur
        const swalContainer = document.querySelector('.swal2-container');
        swalContainer.style.zIndex = '99999';
        swalContainer.style.backdropFilter = 'blur(6px)';
        swalContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';

        // Disable confirm until file chosen
        Swal.getConfirmButton().disabled = true;
        Swal.getConfirmButton().style.opacity = '0.5';

        document.getElementById('swal-before-photo').addEventListener('change', (e) => {
          const file = e.target.files[0];
          const errEl   = document.getElementById('swal-before-error');
          const preview = document.getElementById('swal-before-preview');
          const img     = document.getElementById('swal-before-img');
          const nameEl  = document.getElementById('swal-before-name');

          errEl.style.display   = 'none';
          preview.style.display = 'none';
          Swal.getConfirmButton().disabled = true;
          Swal.getConfirmButton().style.opacity = '0.5';

          if (!file) return;

          const allowedTypes = ['image/jpeg','image/jpg','image/png','image/heic'];
          if (!allowedTypes.includes(file.type) &&
              !file.name.toLowerCase().match(/\.(jpg|jpeg|png|heic)$/)) {
            errEl.textContent   = 'Unsupported photo format. Please upload a JPG, JPEG, or PNG file.';
            errEl.style.display = 'block';
            return;
          }
          if (file.size > 10 * 1024 * 1024) {
            errEl.textContent   = 'The selected photo exceeds the allowed file size (10 MB).';
            errEl.style.display = 'block';
            return;
          }

          const reader = new FileReader();
          reader.onload = (ev) => {
            document.getElementById('dropzone-before-content').style.display = 'none';
            document.getElementById('dropzone-before-label').style.border = '2px solid #10b981';
            document.getElementById('dropzone-before-label').style.background = '#fff';
            img.src               = ev.target.result;
            preview.style.display = 'block';
            nameEl.textContent    = `📎 ${file.name} (${(file.size/1024).toFixed(1)} KB)`;
          };
          reader.readAsDataURL(file);

          Swal.getConfirmButton().disabled = false;
          Swal.getConfirmButton().style.opacity = '1';
        });
      },
      preConfirm: () => {
        const file = document.getElementById('swal-before-photo').files[0];
        if (!file) {
          Swal.showValidationMessage('Please upload a before photo to confirm receipt of this lot.');
          return false;
        }
        return file;
      }
    });

    if (!isConfirmed || !formValues) return;

    // Step 2: Send FormData with photo to backend
    setProcessingId(`confirm_${id}`);
    try {
      const currentISTTime = moment().tz("Asia/Kolkata").format("DD/MM/YYYY : hh:mm A");
      const confirmBy = JSON.parse(localStorage.getItem("user"))?.Name;

      const formData = new FormData();
      formData.append("ID",          id);
      formData.append("ConfirmTime", currentISTTime);
      formData.append("ConfirmBy",   confirmBy);
      formData.append("stage",       "before");
      formData.append("BeforePhoto", formValues);

      await axiosInstance.post("/api/confirm-process", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      Swal.fire('Confirmed!', `Before photo uploaded and lot confirmed successfully!`, 'success');
      fetchProcessData();
    } catch (error) {
      console.error("Error confirming process:", error);
      Swal.fire('Error!', error.response?.data?.error || "The photo could not be uploaded. Please try again.", 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDone = async (processName, id) => {
    // Step 1: Show After Photo upload modal
    const { value: formValues, isConfirmed } = await Swal.fire({
      title: '📷 Upload After Photo',
      html: `
        <div style="font-family: 'Inter', sans-serif; text-align: center;">
          <p style="margin-bottom:16px;color:#64748b;font-size:14px;line-height:1.5;">
            Please capture or upload an <strong>After Photo</strong> to verify completion.
          </p>
          
          <label for="swal-after-photo" id="dropzone-after-label" style="
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            padding: 60px 24px; border: 2px dashed #cbd5e1; border-radius: 16px;
            background: #f8fafc; cursor: pointer; transition: all 0.2s ease; min-height: 250px;
          ">
            <div id="dropzone-after-content" style="display: flex; flex-direction: column; align-items: center;">
              <div style="background: #dbeafe; width: 64px; height: 64px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 16px;">
                <svg style="color: #3b82f6; width: 32px; height: 32px;" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </div>
              <span style="font-size: 16px; font-weight: 700; color: #1e293b;">Tap to Capture Photo</span>
              <span style="font-size: 13px; color: #94a3b8; margin-top: 6px; font-weight: 500;">Supports JPG, PNG, HEIC</span>
            </div>

            <div id="swal-after-preview" style="display: none; width: 100%; text-align: center;">
              <img id="swal-after-img" src="" style="max-height: 220px; max-width: 100%; border-radius: 12px; object-fit: cover; box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);" />
              <div id="swal-after-name" style="margin-top: 16px; font-size: 13px; font-weight: 600; color: #3b82f6; background: #eff6ff; padding: 6px 16px; border-radius: 20px; display: inline-block; border: 1px solid #dbeafe;"></div>
            </div>
          </label>
          
          <input type="file" id="swal-after-photo" accept="image/jpeg,image/jpg,image/png,image/heic" capture="environment" style="display: none;" />
          <div id="swal-after-error" style="color: #ef4444; font-size: 13px; margin-top: 16px; display: none; font-weight: 600; background: #fef2f2; padding: 10px 14px; border-radius: 8px; border: 1px solid #fee2e2;"></div>
        </div>
      `,
      confirmButtonText: '✅ Mark as Done',
      confirmButtonColor: '#0d6efd',
      cancelButtonText: 'Cancel',
      showCancelButton: true,
      focusConfirm: false,
      width: '600px',
      customClass: { popup: 'swal-above-modal' },
      didOpen: () => {
        // Fix z-index so Swal appears above Bootstrap modal, and add backdrop blur
        const swalContainer = document.querySelector('.swal2-container');
        swalContainer.style.zIndex = '99999';
        swalContainer.style.backdropFilter = 'blur(6px)';
        swalContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';

        // Disable confirm until file chosen
        Swal.getConfirmButton().disabled = true;
        Swal.getConfirmButton().style.opacity = '0.5';

        document.getElementById('swal-after-photo').addEventListener('change', (e) => {
          const file = e.target.files[0];
          const errEl   = document.getElementById('swal-after-error');
          const preview = document.getElementById('swal-after-preview');
          const img     = document.getElementById('swal-after-img');
          const nameEl  = document.getElementById('swal-after-name');

          errEl.style.display   = 'none';
          preview.style.display = 'none';
          Swal.getConfirmButton().disabled = true;
          Swal.getConfirmButton().style.opacity = '0.5';

          if (!file) return;

          const allowedTypes = ['image/jpeg','image/jpg','image/png','image/heic'];
          if (!allowedTypes.includes(file.type) &&
              !file.name.toLowerCase().match(/\.(jpg|jpeg|png|heic)$/)) {
            errEl.textContent   = 'Unsupported photo format. Please upload a JPG, JPEG, or PNG file.';
            errEl.style.display = 'block';
            return;
          }
          if (file.size > 10 * 1024 * 1024) {
            errEl.textContent   = 'The selected photo exceeds the allowed file size (10 MB).';
            errEl.style.display = 'block';
            return;
          }

          const reader = new FileReader();
          reader.onload = (ev) => {
            document.getElementById('dropzone-after-content').style.display = 'none';
            document.getElementById('dropzone-after-label').style.border = '2px solid #3b82f6';
            document.getElementById('dropzone-after-label').style.background = '#fff';
            img.src               = ev.target.result;
            preview.style.display = 'block';
            nameEl.textContent    = `📎 ${file.name} (${(file.size/1024).toFixed(1)} KB)`;
          };
          reader.readAsDataURL(file);

          Swal.getConfirmButton().disabled = false;
          Swal.getConfirmButton().style.opacity = '1';
        });
      },
      preConfirm: () => {
        const file = document.getElementById('swal-after-photo').files[0];
        if (!file) {
          Swal.showValidationMessage('Please upload an after photo before completing this lot.');
          return false;
        }
        return file;
      }
    });

    if (!isConfirmed || !formValues) return;

    // Step 2: Send FormData with photo to backend
    setProcessingId(`done_${id}`);
    try {
      const currentISTTime = moment().tz("Asia/Kolkata").format("DD/MM/YYYY : hh:mm A");
      const confirmBy = JSON.parse(localStorage.getItem("user"))?.Name;

      const formData = new FormData();
      formData.append("ID",            id);
      formData.append("CompletedTime", currentISTTime);
      formData.append("ConfirmTime",   currentISTTime);
      formData.append("ConfirmBy",     confirmBy);
      formData.append("stage",         "after");
      formData.append("AfterPhoto",    formValues);

      await axiosInstance.post("/api/completedTime-process", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      Swal.fire('Done!', 'After photo uploaded and lot completed successfully!', 'success');
      fetchProcessData();
    } catch (error) {
      console.error("Error marking process as done:", error);
      Swal.fire('Error!', error.response?.data?.error || "The photo could not be uploaded. Please try again.", 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleAdminDone = async (id) => {
    const result = await Swal.fire({
      title: 'Mark as Done?',
      text: 'Are you sure you want to mark this process as Done?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, mark it!'
    });

    if (!result.isConfirmed) return;

    setProcessingId(`admindone_${id}`); 
    try {
      const currentISTTime = moment().tz("Asia/Kolkata").format("DD/MM/YYYY : hh:mm A");
      const confirmBy = JSON.parse(localStorage.getItem("user"))?.Name;

      await axiosInstance.post("/api/completedTime-process", {
        ID: id,
        CompletedTime: currentISTTime,
        ConfirmBy: confirmBy, 
      });

      Swal.fire('Success!', 'Process marked as Done', 'success');
      const response = await axiosInstance.get("/api/delayed-processes");
      setDelayedProcesses(response.data); 

      const response24hr = await axiosInstance.get("/api/delayed-24hr-processes");
      setDelayed24hrProcesses(response24hr.data); 
    } catch (error) {
      console.error("Error marking process as Done:", error);
      Swal.fire('Error!', error.response?.data?.error || "Failed to mark process as Done. Please try again.", 'error');
    } finally {
      setProcessingId(null); 
    }
  };

  const fetchDelayed24hrProcesses = async () => {
    try {
      const response = await axiosInstance.get("/api/delayed-24hr-processes");
      setDelayed24hrProcesses(response.data);
    } catch (error) {
      console.error("Error fetching 24hr delayed processes:", error);
    }
  };

  useEffect(() => {
    if (user?.Auth === "SuperAdmin") {
      fetchDelayed24hrProcesses();
    }
  }, [user?.Auth]);

  const fetchDispatchOrders = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/dispatch-Notification");
      setDispatchOrders(response.data);
      setDispatchCount(response.data.length); 
    } catch (error) {
      console.error("Error fetching dispatch orders:", error);
    }
  }, []);

  useEffect(() => {
    fetchDispatchOrders();
  }, [fetchDispatchOrders]);

  const handlePartialDispatch = async (id) => {
    const dispatchQty = parseInt(dispatchQtyInput[id]);
    
    const result = await Swal.fire({
      title: 'Confirm Dispatch',
      text: `Are you sure you want to dispatch ${dispatchQty} units?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Dispatch'
    });

    if (!result.isConfirmed) return;
  
    setProcessingId(`dispatch_${id}`); 
    try {
      await axiosInstance.patch(`/api/salesflow/partial-dispatch/${id}`, {
        Dispatch_Quantity: dispatchQty,
      });
      Swal.fire('Success!', `Dispatched ${dispatchQty} units successfully!`, 'success');
      fetchDispatchOrders(); 
      setDispatchQtyInput((prev) => ({ ...prev, [id]: "" })); 
    } catch (error) {
      console.error("Error in partial dispatch:", error);
      Swal.fire('Error!', 'Failed to dispatch. Try again.', 'error');
    } finally {
      setProcessingId(null); 
    }
  };
  
  const handleRemarkChange = (id, value) => {
    setRemarkInput((prev) => ({ ...prev, [id]: value }));
  };

  const handleRemarkSubmit = async (id) => {
    setProcessingId(`remark_${id}`); 
    try {
      const remark = remarkInput[id]; 
      await axiosInstance.patch(`/api/update-dispatch-remark/${id}`, {
        DispatchRemark: remark,
      });
      Swal.fire('Success!', 'Remark updated successfully!', 'success');
      fetchDispatchOrders(); 
    } catch (error) {
      console.error("Error updating remark:", error);
      Swal.fire('Error!', 'Failed to update remark.', 'error');
    } finally {
      setProcessingId(null); 
    }
  };
  
 useEffect(() => {
  if (user?.EmployeeID !== "1017") return;

  const interval = setInterval(async () => {
    try {
      const response = await axiosInstance.get("/api/notifications/panni-packing-completed");
      const newRecords = response.data;

      if (newRecords.length > 0) {
        const filtered = newRecords.filter((note) => !dismissedNotifications.includes(note.ID));
        if (filtered.length > 0) {
          setNewNotifications(filtered);
        }
      }
    } catch (error) {
      console.error("Push notification fetch error:", error);
    }
  }, 10000); 

  return () => clearInterval(interval);
}, [user?.EmployeeID, dismissedNotifications]);

const handleDismissNotification = async (ID) => {
  const srpNo = srpInputs[ID] || "";

  if (!srpNo.trim()) {
    Swal.fire('Warning!', 'Please enter SRP NO before proceeding.', 'warning');
    return;
  }

  setProcessingId(`srp_${ID}`); 
  try {
     await axiosInstance.patch(`/api/notifications/mark-sent/${ID}`, {
      SRP_NO: srpNo.trim(),
    });

    const updatedDismissed = [...dismissedNotifications, ID];
    setDismissedNotifications(updatedDismissed);
    localStorage.setItem("dismissedNotifications", JSON.stringify(updatedDismissed));
    setNewNotifications((prev) => prev.filter((n) => n.ID !== ID));
  } catch (error) {
    console.error("Error marking notification as sent:", error);
    Swal.fire('Error!', 'Failed to update SRP.', 'error');
  } finally {
    setProcessingId(null); 
  }
};

function formatDuration(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function getRemainingWorkingSeconds(startTime, totalHours) {
  const WORK_START = 10; 
  const WORK_END = 19;   

  let current = moment(startTime);
  let remainingSeconds = totalHours * 3600;
  const now = moment();

  while (current.isBefore(now) && remainingSeconds > 0) {
    const workStart = current.clone().hour(WORK_START).minute(0).second(0);
    const workEnd = current.clone().hour(WORK_END).minute(0).second(0);

    if (current.isBefore(workStart)) {
      current = workStart;
    } else if (current.isSameOrAfter(workEnd)) {
      current.add(1, "day").hour(WORK_START).minute(0).second(0);
    } else {
      const effectiveEnd = moment.min(workEnd, now);
      const seconds = effectiveEnd.diff(current, "seconds");
      remainingSeconds -= seconds;
      current = effectiveEnd;
    }
  }

  return Math.max(remainingSeconds, 0);
}

useEffect(() => {
  const interval = setInterval(() => {
    const updated = {};
      [...processData, ...delayedProcesses, ...delayed24hrProcesses].forEach((item) => {
      const startTime = moment(item.Updated_Time, "DD/MM/YYYY : hh:mm A");
      const seconds = getRemainingWorkingSeconds(startTime, item.Remaining_Hours);  
      updated[item.ID] = seconds;
    });

    setLiveRemainingTimes(updated);
  }, 1000); 

  return () => clearInterval(interval);
}, [processData,  delayedProcesses, delayed24hrProcesses]);


  return (
    <div>
    <div id="alertPlaceholder"></div>
    <style>{`
      @keyframes slideInRight {
        from { transform: translateX(120%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }

      @keyframes slideOutLeft {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(-120%); opacity: 0; }
      }

      .notification {
        animation: slideInRight 0.4s ease forwards;
        transition: all 0.4s ease;
      }

      .notification.hide {
        animation: slideOutLeft 0.4s ease forwards;
      }
    `}</style>
       <div
        style={{
          position: "fixed",
          top: "10px",
          bottom: "10px",
          right: "20px",
          zIndex: 9999,
          maxHeight: "100%",   
          overflowY: "auto",   
          paddingRight: "10px"
        }}
      >
      {newNotifications.map((note, idx) => (
        <div
          key={note.ID}
          className={`alert alert-info alert-dismissible fade show  notification ${
            dismissedNotifications.includes(note.ID) ? "hide" : ""
          }`}
          style={{
            marginBottom: "10px", 
            minWidth: "320px",
            padding: "15px",
            borderRadius: "8px",
          }}
          role="alert"
        >
          📦 <strong>PANNI PACKING DONE</strong><br />
          <strong>LOT:</strong> {note["LOT ID"]} <br />
          <strong>Qty:</strong> {note.QUANTITY} <br />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "9px" }}>
          <input
              type="text"
              placeholder="Enter SRP NO"
              className="form-control form-control-sm me-2"
              value={srpInputs[note.ID] || ""}
              onChange={(e) =>
                setSrpInputs((prev) => ({ ...prev, [note.ID]: e.target.value }))
              }
              style={{width:"auto"}}
              disabled={processingId !== null}
            />
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={() => handleDismissNotification(note.ID)}
              disabled={processingId !== null}
            >
              {processingId === `srp_${note.ID}` ? (
                <><span className="spinner-border spinner-border-sm me-1"></span>Processing...</>
              ) : "SRP DONE"}
            </button>
          </div>
        </div>
      ))}
      </div>

      <nav className="navbar navbar-expand-lg bg-body-tertiary">
        <div className="container-fluid">
          <button
            className="btn btn-link toggleBtn"
            onClick={toggleSidebar}
            style={{ fontSize: "24px", border: "none", cursor: "pointer", marginRight: "15px", marginLeft: "10px" }}
          >
            <i className={`bi ${isSidebarVisible ? "bi-list" : "bi-x-lg"}`}></i>
          </button>

          <a className="navbar-brand">
            <img src={logo} alt="Fibre Bond Logo" style={{ height: "40px", width: "auto", display: "none" }} />
          </a>

          <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent" >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="navbarSupportedContent">
         <ul className="navbar-nav me-auto mb-2 mb-lg-0">
           {(user?.Auth === "Supervisor" || ["4","1021"].includes(user?.EmployeeID)) && (
            <>
              <li className="nav-item d-lg-none">
                <NavLink className="nav-link" to="/Bill-Upload">Bill Upload</NavLink>
              </li>
              <li className="nav-item d-lg-none">
                <NavLink className="nav-link" to="/RM-Return">RM Return</NavLink>
              </li>
              <li className="nav-item d-lg-none">
                <NavLink className="nav-link" to="/sales-form">Sales Form</NavLink>
              </li>
            </>
          )}
        </ul>

            <span style={{ marginLeft: "-21rem" }}>
            {user?.Auth === "Supervisor" && ["MONU", "Sudhir CK"].includes(user?.Name) && (
            <a name="Dispatch" id="Dispatch" className="btn" href="#" role="button" onClick={() => setShowDispatchModal(true)}>
               <span style={{ color: "red" }}>{dispatchCount}</span>
                <img src={Dispatch} className="img-fluid rounded-top" alt="Dispatch Icon" style={{ height: "20px", width: "34px" }} />
              </a>
              )}
              
              {user?.Auth === "Supervisor" && (
                <a
                  name="WorkerAllocationNotification"
                  className="btn"
                  href="#"
                  role="button"
                  title="Worker Confirmation"
                  onClick={() => setShowAllocationModal(true)}
                >
                  <span style={{ color: "red", fontWeight: "bold" }}>{workerAllocations.length}</span>
                  <img
                    src={User}
                    className="img-fluid rounded-circle shadow-sm border border-secondary"
                    alt="Worker Icon"
                    style={{ height: "24px", width: "24px", marginLeft:"2px" }}
                  />
                </a>
              )}

              {user?.Auth === "Supervisor" && (
                <a name="Notification" id="Notification" className="btn" href="#" role="button" onClick={() => setShowModal(true)}>
                  <span style={{ color: "red" }}>{processData.length}</span>
                  <img src={Bell} className="img-fluid rounded-top" alt="Bell Icon" style={{ height: "20px", width: "20px" }} />
                </a>
              )}
              {user?.Auth === "Admin" && (
                <a name="AdminNotification" id="AdminNotification" className="btn" href="#" role="button" onClick={() => setShowDelayedModal(true)}>
                  <span style={{ color: "red" }}>{delayedProcesses.length}</span>
                  <img src={Bell} className="img-fluid rounded-top" alt="Bell Icon" style={{ height: "20px", width: "20px" }} />
                </a>
              )}
              {user?.Auth === "SuperAdmin" && user?.Name !== "Sandeep Maintenance" && (
                <a name="SuperAdminNotification" id="SuperAdminNotification" className="btn" href="#" role="button" onClick={() => setShowDelayed24hrModal(true)}>
                  <span style={{ color: "red" }}>{delayed24hrProcesses.length}</span>
                  <img src={Bell} className="img-fluid rounded-top" alt="Bell Icon" style={{ height: "20px", width: "20px" }} />
                </a>
              )}
              Welcome, {user?.Name}!
            </span>
            <div className="dropdown">
              <a className="d-flex align-items-center text-decoration-none dropdown-toggle ml-5" role="button" data-bs-toggle="dropdown" aria-expanded="false" style={{ marginRight: "20px", justifyContent: "end" }}>
                <img src={User} alt="Profile" className="rounded-circle" style={{ width: "40px", height: "40px", objectFit: "cover" }} />
              </a>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><a className="dropdown-item text-danger" onClick={logout} style={{ cursor: "pointer" }}>Logout</a></li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      {showAllocationModal && (() => {
        const filteredWorkers = workerAllocations.filter((alloc) => {
          const searchLower = searchTerm.toLowerCase();
          const matchesSearch =
            (alloc.Worker_Name || "").toLowerCase().includes(searchLower) ||
            (alloc.From_Dep || "").toLowerCase().includes(searchLower) ||
            (alloc.ID || "").toString().includes(searchLower);

          const currentStatus = alloc.Status || "Pending";
          const matchesStatus = statusFilter === "All" || currentStatus === statusFilter;

          return matchesSearch && matchesStatus;
        });

        return (
          <div 
            className="modal fade show" 
            tabIndex="-1" 
            style={{ 
              display: "block", 
              backgroundColor: "rgba(0,0,0,0.6)", 
              zIndex: 1055, 
              position: "fixed", 
              top: 0, left: 0, width: "100%", height: "100%",
              backdropFilter: "blur(5px)" 
            }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content shadow-lg border-0 rounded-4">
                
                {/* Modal Header */}
                <div className="modal-header bg-primary bg-gradient text-white border-0 rounded-top-4 px-4 py-3">
                  <h5 className="modal-title d-flex align-items-center gap-2 fw-bold mb-0" style={{ letterSpacing: "0.5px" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" className="bi bi-people-fill" viewBox="0 0 16 16">
                      <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6m-5.784 6A2.24 2.24 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.3 6.3 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1zM4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5"/>
                    </svg>
                    Worker Confirmation
                  </h5>
                  <button type="button" className="btn-close btn-close-white opacity-75 shadow-none" onClick={() => setShowAllocationModal(false)}></button>
                </div>

                {/* Modal Body */}
                <div className="modal-body p-4 bg-light">
                  
                  {/* Filters Section */}
                  {workerAllocations.length > 0 && (
                    <div className="row mb-4 align-items-center bg-white p-3 rounded-3 shadow-sm border mx-0">
                      <div className="col-md-8 mb-3 mb-md-0">
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search text-muted" viewBox="0 0 16 16">
                              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                            </svg>
                          </span>
                          <input
                            type="text"
                            className="form-control border-start-0 bg-light shadow-none"
                            placeholder="Search by ID, Name, or Department..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select bg-light shadow-none fw-medium text-secondary"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="All">View All Statuses</option>
                          <option value="Pending">Pending Arrivals</option>
                          <option value="Occupied">Currently Occupied</option>
                          <option value="Completed">Completed</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Table Section */}
                  {workerAllocations.length === 0 ? (
                    // Case 1: No workers assigned at all
                    <div className="text-center py-5 bg-white rounded-3 shadow-sm border">
                      <div className="p-3 bg-light rounded-circle d-inline-block mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" fill="currentColor" className="bi bi-inbox text-primary opacity-75" viewBox="0 0 16 16">
                          <path d="M4.98 4a.5.5 0 0 0-.39.188L1.54 8H6a.5.5 0 0 1 .5.5 1.5 1.5 0 1 0 3 0A.5.5 0 0 1 10 8h4.46l-3.05-3.812A.5.5 0 0 0 11.02 4zM3.81.563A1.5 1.5 0 0 1 4.98 0h6.04a1.5 1.5 0 0 1 1.17.563l3.7 4.625a.5.5 0 0 1 .106.304l-.228 6.203a1.5 1.5 0 0 1-1.498 1.305H1.73a1.5 1.5 0 0 1-1.498-1.305L.004 5.492a.5.5 0 0 1 .106-.304zM12.18 1H3.82L1 4.5V14h14V4.5z"/>
                        </svg>
                      </div>
                      <h5 className="fw-bold text-dark">No Pending Workers</h5>
                      <p className="text-muted mb-0">When someone assigns a worker to your department, they will appear here.</p>
                    </div>
                  ) : filteredWorkers.length === 0 ? (
                    // Case 2: Workers exist, but filtered out
                    <div className="text-center py-5 bg-white rounded-3 shadow-sm border">
                      <h5 className="fw-bold text-secondary">No matching results</h5>
                      <p className="text-muted mb-0">Try adjusting your search or filters.</p>
                    </div>
                  ) : (
                    // Case 3: Data table
                    <div className="table-responsive rounded-3 shadow-sm border bg-white overflow-hidden">
                      <table className="table table-hover text-center align-middle mb-0">
                        <thead className="bg-dark text-white">
                          <tr>
                            <th className="py-3 text-white fw-semibold border-0">ID</th>
                            <th className="py-3 text-white fw-semibold border-0">From Department</th>
                            <th className="py-3 text-white fw-semibold border-0">Worker Name</th>
                            <th className="py-3 text-white fw-semibold border-0">Status</th>
                            <th className="py-3 text-white fw-semibold border-0">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredWorkers.map((alloc) => (
                            <tr key={alloc.ID} style={{ transition: "all 0.2s ease" }}>
                              <td className="fw-bold text-muted">#{alloc.ID}</td>
                              <td className="fw-medium text-dark">{alloc.From_Dep}</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px", fontSize: "14px", fontWeight: "bold" }}>
                                    {(alloc.Worker_Name || "W").charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-primary fw-bold">{alloc.Worker_Name}</span>
                                </div>
                              </td>
                              <td>
                                <span className={`badge px-3 py-2 rounded-pill fw-semibold shadow-sm d-inline-flex align-items-center gap-1 border ${
                                  alloc.Status?.trim() === 'Occupied' ? 'bg-warning text-dark border-warning' : 
                                  alloc.Status?.trim() === 'Completed' ? 'bg-success bg-opacity-10 text-success border-success' : 
                                  'bg-info bg-opacity-10 text-info border-info'
                                }`}>
                                  
                                  {alloc.Status?.trim() === 'Occupied' ? (
                                    <>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-building-fill" viewBox="0 0 16 16">
                                        <path d="M3 0a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h3v-3.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V16h3a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1H3Zm1 2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm3.5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5ZM4 5.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm3.5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5ZM4 8.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm3.5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5Zm-7 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm3.5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5Z"/>
                                      </svg>
                                      Occupied
                                    </>
                                  ) : alloc.Status?.trim() === 'Completed' ? (
                                    <>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-check-all" viewBox="0 0 16 16">
                                        <path d="M8.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L2.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093L8.95 4.992a.252.252 0 0 1 .02-.022zm-.92 5.14.92.92a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 1 0-1.091-1.028L9.477 9.417l-.485-.486-.943 1.179z"/>
                                      </svg>
                                      Completed
                                    </>
                                  ) : (
                                    <>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-hourglass-split" viewBox="0 0 16 16">
                                        <path d="M2.5 15a.5.5 0 1 1 0-1h1v-1a4.5 4.5 0 0 1 2.557-4.06c.29-.139.443-.377.443-.59v-.7c0-.213-.154-.451-.443-.59A4.5 4.5 0 0 1 3.5 3V2h-1a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-1v1a4.5 4.5 0 0 1-2.557 4.06c-.29.139-.443.377-.443.59v.7c0 .213.154.451.443.59A4.5 4.5 0 0 1 12.5 14v1h1a.5.5 0 0 1 0 1h-11zm2-13v1c0 .537.12 1.045.337 1.5h6.326c.216-.455.337-.963.337-1.5V2h-7zm3 6.35c0 .701-.478 1.236-1.011 1.492A3.5 3.5 0 0 0 4.5 13s.866-1.299 3-1.48V8.35zm1 0v3.17c2.134.181 3 1.48 3 1.48a3.5 3.5 0 0 0-1.989-3.158C8.978 9.586 8.5 9.052 8.5 8.35z"/>
                                      </svg>
                                      Pending
                                    </>
                                  )}
                                </span>
                              </td>
                              <td>
                                {/* PRD Step 4: Confirm Arrival */}
                                {(!alloc.Status || alloc.Status === "Pending") && (
                                  <button
                                    className="btn btn-success btn-sm px-4 d-inline-flex align-items-center justify-content-center gap-2 shadow-sm rounded-pill"
                                    onClick={() => handleAcceptWorker(alloc.ID)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-check-circle-fill" viewBox="0 0 16 16">
                                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                    </svg>
                                    Confirm Arrival
                                  </button>
                                )}

                                {/* PRD Step 5a: ONLY Work Completed */}
                                {alloc.Status === "Occupied" && (
                                  <button
                                    className="btn btn-primary btn-sm px-4 d-inline-flex align-items-center justify-content-center gap-2 shadow-sm rounded-pill"
                                    onClick={() => handleMarkCompleted(alloc.ID)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-play-circle-fill" viewBox="0 0 16 16">
                                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.79 5.093A.5.5 0 0 0 6 5.5v5a.5.5 0 0 0 .79.407l3.5-2.5a.5.5 0 0 0 0-.814l-3.5-2.5z"/>
                                    </svg>
                                    Mark Completed
                                  </button>
                                )}

                                {/* PRD Step 5b: Satisfaction Feedback (Appears after completion) */}
                                {alloc.Status?.trim() === "Completed" && !alloc.satisfaction_status && (
                                  <div className="mt-2 p-3 bg-light border border-warning rounded-3 shadow-sm d-flex flex-column align-items-center transition-all">
                                    
                                    {/* Feedback Pending Badge */}
                                    <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill d-inline-flex align-items-center gap-2 shadow-sm" style={{ fontSize: "0.85rem" }}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-star-fill text-dark" viewBox="0 0 16 16">
                                        <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
                                      </svg>
                                      Action Required: Rate Worker
                                    </span>

                                    {/* Buttons Container */}
                                    <div className="d-flex flex-wrap gap-2 justify-content-center w-100">
                                      
                                      {/* Satisfied Button */}
                                      <button
                                        className="btn btn-success btn-sm px-3 py-2 d-inline-flex align-items-center justify-content-center gap-2 shadow-sm rounded-pill fw-semibold flex-grow-1 hover-zoom"
                                        onClick={() => handleSatisfaction(alloc.ID, "Satisfied")}
                                        disabled={processingId !== null}
                                        style={{ minWidth: "120px" }}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-hand-thumbs-up-fill" viewBox="0 0 16 16">
                                          <path d="M6.956 1.745C7.021.81 7.908.087 8.864.325l.261.066c.463.116.874.456 1.012.965.22.816.533 2.511.062 4.51a10 10 0 0 1 .443-.051c.713-.065 1.669-.072 2.516.21.518.173.994.681 1.2 1.273.184.532.16 1.162-.234 1.733q.086.18.138.363c.077.27.113.567.113.856s-.036.586-.113.856c-.039.135-.09.273-.16.404.169.387.107.82-.003 1.148a3.2 3.2 0 0 1-.488.901c.054.152.076.312.076.465 0 .305-.089.625-.253.912C13.1 15.522 12.437 16 11.5 16H8c-.605 0-1.07-.081-1.466-.218a4.8 4.8 0 0 1-.97-.484l-.048-.03c-.504-.307-.999-.609-2.068-.722C2.682 14.464 2 13.846 2 13V9c0-.85.685-1.432 1.357-1.615.849-.232 1.574-.787 2.132-1.41.56-.627.914-1.28 1.039-1.639.199-.575.356-1.539.428-2.59z"/>
                                        </svg>
                                        Satisfied
                                      </button>

                                      {/* Not Satisfied Button */}
                                      <button
                                        className="btn btn-danger btn-sm px-3 py-2 d-inline-flex align-items-center justify-content-center gap-2 shadow-sm rounded-pill fw-semibold flex-grow-1 hover-zoom"
                                        onClick={() => handleSatisfaction(alloc.ID, "Not Satisfied")}
                                        disabled={processingId !== null}
                                        style={{ minWidth: "120px" }}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-hand-thumbs-down-fill" viewBox="0 0 16 16">
                                          <path d="M6.956 14.255c.065.935.952 1.658 1.908 1.42l.261-.065a1.38 1.38 0 0 0 1.012-.965c.22-.816.533-2.512.062-4.51.136.02.285.037.443.051.713.065 1.669.071 2.516-.211.518-.173.994-.68 1.2-1.272a1.9 1.9 0 0 0-.234-1.734c.058-.118.103-.242.138-.362.077-.27.113-.568.113-.857 0-.288-.036-.585-.113-.856a2 2 0 0 0-.16-.403c.169-.387.107-.82-.003-1.148a3.2 3.2 0 0 0-.488-.901c.054-.152.076-.312.076-.465 0-.305-.089-.625-.253-.912C13.1.478 12.437 0 11.5 0H8c-.605 0-1.07.081-1.466.218a4.8 4.8 0 0 0-.97.484l-.048.03c-.504.307-.999.609-2.068.722C2.682 1.536 2 2.154 2 3v4c0 .85.685 1.432 1.357 1.615.849.232 1.574.787 2.132 1.41.56.626.914 1.28 1.039 1.638.199.575.356 1.539.428 2.59z"/>
                                        </svg>
                                        Not Satisfied
                                      </button>

                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                </div>
                
                {/* Modal Footer */}
                <div className="modal-footer border-0 bg-light rounded-bottom-4 px-4 py-3">
                  <button className="btn btn-secondary px-5 rounded-pill shadow-sm fw-medium" onClick={() => setShowAllocationModal(false)} disabled={processingId !== null}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showModal && (
        <div className="modal show fade-in" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(5px)" }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: "1300px" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Process Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="d-flex mb-3 gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Filter by Process Name or Item Name"
                    value={processItemFilter}
                    onChange={(e) => setProcessItemFilter(e.target.value)}
                  />
                  <button className="btn btn-outline-secondary" onClick={() => setProcessItemFilter("")}>Clear</button>
                </div>
                <div className="table-responsive border rounded">
                  <table className="table table-hover mb-0 text-center align-middle">
                  <thead className="bg-dark text-white">
                    <tr>
                      <th className="py-3 text-white fw-semibold border-0">Process Name</th>
                      <th className="py-3 text-white fw-semibold border-0">Item Name</th>
                      <th className="py-3 text-white fw-semibold border-0">Quantity</th>
                      <th className="py-3 text-white fw-semibold border-0">Total Hours</th>
                      <th className="py-3 text-white fw-semibold border-0">Remaining Time</th>
                      <th className="py-3 text-white fw-semibold border-0">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProcessData.length > 0 ? (
                      filteredProcessData.map((process, index) => (
                        <tr key={index}>
                        <td>{process["PROCESS NAME"]}</td>
                        <td>{process["ITEM NAME"]}</td>
                        <td>{process["QUANTITY"]}</td>
                        <td>{process["Remaining_Hours"] || "N/A"}</td>
                        <td>
                          {liveRemainingTimes[process.ID] !== undefined
                            ? formatDuration(liveRemainingTimes[process.ID])
                            : "Calculating..."}
                        </td>
                        <td>
                          {process.ConfirmTime == null ? (
                            <button 
                              className="btn btn-outline-success" 
                              onClick={() => handleConfirm(process["PROCESS NAME"], process.ID)}
                              disabled={processingId !== null}
                            >
                              {processingId === `confirm_${process.ID}` ? <><span className="spinner-border spinner-border-sm me-1"></span>Confirming...</> : "Confirm"}
                            </button>
                          ) : process.CompletedTime == null ? (
                            <button 
                              className="btn btn-outline-primary" 
                              onClick={() => handleDone(process["PROCESS NAME"], process.ID)}
                              disabled={processingId !== null}
                            >
                              {processingId === `done_${process.ID}` ? <><span className="spinner-border spinner-border-sm me-1"></span>Processing...</> : "Done"}
                            </button>
                          ) : (
                            <span>Completed</span>
                          )}
                        </td>
                      </tr>
                    ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">No processes found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
              <div className="modal-footer border-0 bg-light rounded-bottom-4 px-4 py-3">
                <button className="btn btn-secondary px-5 rounded-pill shadow-sm fw-medium" onClick={() => setShowModal(false)} disabled={processingId !== null}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDelayedModal && (
        <div className="modal show fade-in" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(5px)" }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: "1300px" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delayed Processes</h5>
                <button type="button" className="btn-close" onClick={() => setShowDelayedModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="d-flex mb-3 gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Filter by Supervisor, Process Name, or Item Name"
                    value={delayedFilter}
                    onChange={(e) => setDelayedFilter(e.target.value)}
                  />
                  <button className="btn btn-outline-secondary" onClick={() => setDelayedFilter("")}>Clear</button>
                </div>
                <div className="table-responsive border rounded">
                  <table className="table table-hover mb-0 text-center align-middle">
                    <thead className="bg-dark text-white">
                      <tr>
                        <th className="py-3 text-white fw-semibold border-0">Supervisor</th>
                        <th className="py-3 text-white fw-semibold border-0">Process Name</th>
                        <th className="py-3 text-white fw-semibold border-0">Item Name</th>
                        <th className="py-3 text-white fw-semibold border-0">Quantity</th>
                        <th className="py-3 text-white fw-semibold border-0">New Process Time</th>
                        <th className="py-3 text-white fw-semibold border-0">Status</th>
                        <th className="py-3 text-white fw-semibold border-0">Total Hours</th>
                        <th className="py-3 text-white fw-semibold border-0">Remaining Time</th>
                        <th className="py-3 text-white fw-semibold border-0">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDelayedProcesses.length > 0 ? (
                        filteredDelayedProcesses.map((process, index) => (
                          <tr key={index}>
                            <td>{process["SupervisorName"]}</td>
                            <td>{process["PROCESS NAME"]}</td>
                            <td>{process["ITEM NAME"]}</td>
                            <td>{process["Quantity"]}</td>
                            <td>{process["Updated_Time"]}</td>
                            <td>
                              {process["ProcessDelayNew"] === "Yes" ? "Process Incomplete" : process["ConfirmDelay"] === "Yes" ? "Confirm Incomplete" : ""}
                            </td>
                             <td>{process["Remaining_Hours"] || "N/A"}</td>
                               <td>
                                {liveRemainingTimes[process.ID] !== undefined ? formatDuration(liveRemainingTimes[process.ID]) : "Calculating..."}
                              </td>
                            <td>
                              <button 
                                className="btn btn-success btn-sm px-4 d-inline-flex align-items-center justify-content-center gap-2 shadow-sm rounded-pill" 
                                onClick={() => handleAdminDone(process["ID"])}
                                disabled={processingId !== null}
                              >
                                {processingId === `admindone_${process["ID"]}` ? <><span className="spinner-border spinner-border-sm me-1"></span>Processing...</> : "Done"}
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center py-4 text-muted">No processes found matching your search.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer border-0 bg-light rounded-bottom-4 px-4 py-3">
                <button className="btn btn-secondary px-5 rounded-pill shadow-sm fw-medium" onClick={() => setShowDelayedModal(false)} disabled={processingId !== null}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDelayed24hrModal && (
        <div className="modal show fade-in" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(5px)" }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: "1300px" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">24-Hour Delayed Processes</h5>
                <button type="button" className="btn-close" onClick={() => setShowDelayed24hrModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="d-flex mb-3 gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Filter by Supervisor, Process Name, or Item Name"
                    value={delayed24hrFilter}
                    onChange={(e) => setDelayed24hrFilter(e.target.value)}
                  />
                  <button className="btn btn-outline-secondary" onClick={() => setDelayed24hrFilter("")}>Clear</button>
                </div>
                <div className="table-responsive border rounded">
                  <table className="table table-hover mb-0 text-center align-middle">
                    <thead className="bg-dark text-white">
                      <tr>
                        <th className="py-3 text-white fw-semibold border-0">Supervisor</th>
                        <th className="py-3 text-white fw-semibold border-0">Process Name</th>
                        <th className="py-3 text-white fw-semibold border-0">Item Name</th>
                        <th className="py-3 text-white fw-semibold border-0">Quantity</th>
                        <th className="py-3 text-white fw-semibold border-0">New Process Time</th>
                        <th className="py-3 text-white fw-semibold border-0">Total Hours</th>
                        <th className="py-3 text-white fw-semibold border-0">Remaining Time</th>
                        <th className="py-3 text-white fw-semibold border-0">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDelayed24hrProcesses.length > 0 ? (
                        filteredDelayed24hrProcesses.map((process, index) => (
                          <tr key={index}>
                            <td>{process["SupervisorName"]}</td>
                            <td>{process["PROCESS NAME"]}</td>
                            <td>{process["ITEM NAME"]}</td>
                            <td>{process["Quantity"]}</td>
                            <td>{process["Updated_Time"]}</td>
                            <td>{process["Remaining_Hours"] || "N/A"}</td>
                              <td>
                              {liveRemainingTimes[process.ID] !== undefined ? formatDuration(liveRemainingTimes[process.ID]) : "Calculating..."}
                            </td>
                            <td>
                              <button 
                                className="btn btn-success btn-sm px-4 d-inline-flex align-items-center justify-content-center gap-2 shadow-sm rounded-pill" 
                                onClick={() => handleAdminDone(process["ID"])}
                                disabled={processingId !== null}
                              >
                                {processingId === `admindone_${process["ID"]}` ? <><span className="spinner-border spinner-border-sm me-1"></span>Processing...</> : "Done"}
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">No processes found matching your search.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer border-0 bg-light rounded-bottom-4 px-4 py-3">
                <button className="btn btn-secondary px-5 rounded-pill shadow-sm fw-medium" onClick={() => setShowDelayed24hrModal(false)} disabled={processingId !== null}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
       {showDispatchModal && (
        <div className="modal show fade-in" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(5px)" }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: "1300px" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ready for Dispatch</h5>
                <button className="btn-close" onClick={() => setShowDispatchModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="d-flex mb-3 gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Filter by Lot ID"
                    value={lotIdFilter}
                    onChange={(e) => setLotIdFilter(e.target.value)}
                  />
                  <button className="btn btn-outline-secondary" onClick={() => setLotIdFilter("")}>Clear</button>
                </div>
                <div className="table-responsive border rounded">
                  <table className="table table-hover mb-0 text-center align-middle">
                  <thead className="bg-dark text-white">
                    <tr>
                      <th className="py-3 text-white fw-semibold border-0">Lot ID</th>
                      <th className="py-3 text-white fw-semibold border-0">GRN No</th>
                      <th className="py-3 text-white fw-semibold border-0">Received Date</th>
                      <th className="py-3 text-white fw-semibold border-0">Quantity</th>
                      <th className="py-3 text-white fw-semibold border-0">Invoice</th>
                      <th className="py-3 text-white fw-semibold border-0">Status</th>
                      <th className="py-3 text-white fw-semibold border-0">Add Remark</th>
                      <th className="py-3 text-white fw-semibold border-0">Dispatch Details</th>
                      <th className="py-3 text-white fw-semibold border-0">Action</th>
                    </tr>
                  </thead>
                  <tbody>

                {filteredDispatchOrders.length > 0 ? (
                      filteredDispatchOrders.map((order) => (
                        <tr key={order.ID}>
                          <td>{order["LOT ID"]}</td>
                          <td>{order["SRP NO"]}</td>
                          <td>{order["RECEIVED TIME"]}</td>
                          <td>{order["QUANTITY"]}</td>
                          <td>{order["Invoice_Number"]}</td>
                          <td>{order["SO_Status"]}</td>
                          <td>
                          <input
                            type="text"
                            className="form-control"
                            value={remarkInput[order.ID] ?? order.DispatchRemark ?? ""}
                            onChange={(e) => handleRemarkChange(order.ID, e.target.value)}
                            placeholder="Add a remark"
                            disabled={processingId !== null}
                          />
                          <button 
                            className="btn btn-primary btn-sm mt-2" 
                            onClick={() => handleRemarkSubmit(order.ID)}
                            disabled={processingId !== null}
                          >
                            {processingId === `remark_${order.ID}` ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : "Save"}
                          </button>
                        </td>
                        <td>{order["DispatchDetails"]}</td>
                       <td>
                       <input
                            type="number"
                            className="form-control mb-2"
                            placeholder="Enter dispatch qty"
                            min={1}
                            max={order.QUANTITY}
                            value={dispatchQtyInput[order.ID] || ""}
                            onChange={(e) => handleDispatchQtyChange(order.ID, e.target.value)}
                            disabled={processingId !== null}
                          />
                       <button
                            className="btn btn-success btn-sm"
                            onClick={() => handlePartialDispatch(order.ID)}
                            disabled={ processingId !== null || !dispatchQtyInput[order.ID] || isNaN(dispatchQtyInput[order.ID]) || parseInt(dispatchQtyInput[order.ID]) <= 0 || parseInt(dispatchQtyInput[order.ID]) > order.QUANTITY }
                          >
                            {processingId === `dispatch_${order.ID}` ? <><span className="spinner-border spinner-border-sm me-1"></span>Dispatching...</> : "Dispatch"}
                          </button>
                       </td>
                        </tr>
                      ))
                     ) : (
                      <tr>
                      <td colSpan="9" className="text-center">No orders ready for dispatch.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
              <div className="modal-footer border-0 bg-light rounded-bottom-4 px-4 py-3">
                <button className="btn btn-secondary px-5 rounded-pill shadow-sm fw-medium" onClick={() => setShowDispatchModal(false)} disabled={processingId !== null}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;